/**
 * ChronicleDetailSections — DEV-1336 (rehome legacy chronicle content).
 *
 * Read-display of the four legacy content areas — Key Players, Timeline,
 * Outcomes, and Links — lifted out of the retiring ChronicleViewPage so they
 * survive on the new PillarPageShell detail surface. Each section renders only
 * when it has data. Team reuses the shared FlipTeamMemberCard (same component
 * Mini-Project Team uses → training-ground parity).
 *
 * Editing still lives in the legacy editor until it is rehomed (before the
 * editor retires, DEV-1343). This component is presentational only.
 */

import FlipTeamMemberCard from '../TeamMemberCard/FlipTeamMemberCard';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { ChronicleResponse } from '../../api/chronicles';
import styles from './ChronicleDetailSections.module.css';

export type ChronicleSectionKey = 'team' | 'timeline' | 'outcomes' | 'links' | 'news';
const ALL_SECTIONS: ChronicleSectionKey[] = ['team', 'timeline', 'outcomes', 'links', 'news'];

interface Props {
  chronicle: ChronicleResponse;
  /** Which sections to render (default: all). Lets each tab show its subset. */
  include?: ChronicleSectionKey[];
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return iso; }
}

export default function ChronicleDetailSections({ chronicle, include = ALL_SECTIONS }: Props) {
  const { t } = useAppTranslation('pages');
  const show = (k: ChronicleSectionKey) => include.includes(k);

  const sortedTeam = show('team') ? [...(chronicle.teamCredits || [])].sort((a, b) => a.sortOrder - b.sortOrder) : [];
  const sortedTimeline = show('timeline') ? [...(chronicle.timelineEvents || [])].sort((a, b) => a.sortOrder - b.sortOrder) : [];
  const sortedLinks = (show('links') || show('news')) ? [...(chronicle.externalLinks || [])].sort((a, b) => a.sortOrder - b.sortOrder) : [];
  const newsLinks = show('news') ? sortedLinks.filter((l) => l.linkType === 'news') : [];
  const otherLinks = show('links') ? sortedLinks.filter((l) => l.linkType !== 'news') : [];
  const sortedMetrics = show('outcomes') ? [...(chronicle.outcomeMetrics || [])].sort((a, b) => a.sortOrder - b.sortOrder) : [];

  const resolveEventDate = (ev: { eventDate: string | null; dateLabel: string }) =>
    ev.eventDate ? fmtDate(ev.eventDate) : (ev.dateLabel || null);

  const hasAnything =
    sortedTeam.length > 0 || sortedTimeline.length > 0 || sortedMetrics.length > 0
    || newsLinks.length > 0 || otherLinks.length > 0;
  if (!hasAnything) return null;

  return (
    <>
      {/* ── Key Players ── */}
      {sortedTeam.length > 0 && (
        <section className={styles.proseSection}>
          <h2 className={styles.proseSectionTitle}>{t('chronicleView.keyPlayersHeading')}</h2>
          <div className={styles.teamGrid}>
            {sortedTeam.map((tc) => (
              <FlipTeamMemberCard
                key={tc.creditGuid}
                displayName={tc.personName}
                photoUrl={tc.manualPhotoUrl}
                roleTitle={tc.roleTitle}
                linkedUserGuid={tc.userGuid}
                userGuid={tc.userGuid}
                contactProfileUrl={tc.externalProfileUrl}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Timeline ── */}
      {sortedTimeline.length > 0 && (
        <section className={styles.proseSection}>
          <h2 className={styles.proseSectionTitle}>{t('chronicleView.tabTimeline')}</h2>
          <div className={styles.timeline}>
            {sortedTimeline.map((ev) => {
              const status = ev.eventStatus || 'upcoming';
              const dotClass =
                status === 'completed' ? styles.timelineDotCompleted
                : status === 'current' ? styles.timelineDotCurrent
                : styles.timelineDotUpcoming;
              return (
                <div key={ev.eventGuid} className={`${styles.timelineItem} ${status === 'completed' ? styles.timelineItemCompleted : ''}`}>
                  <div className={`${styles.timelineDot} ${dotClass}`} />
                  {(resolveEventDate(ev) || ev.eventTime) && (
                    <div className={styles.timelineDateLabel}>
                      {resolveEventDate(ev)}
                      {ev.eventTime && <span className={styles.timelineTime}> · {ev.eventTime}</span>}
                    </div>
                  )}
                  <div className={styles.timelineEventTitle}>{ev.eventTitle}</div>
                  {ev.address && <div className={styles.timelineAddress}>{ev.address}</div>}
                  {ev.description && <div className={styles.timelineDesc}>{ev.description}</div>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Outcomes ── */}
      {sortedMetrics.length > 0 && (
        <section className={styles.proseSection}>
          <h2 className={styles.proseSectionTitle}>{t('chronicleView.detailsMetrics')}</h2>
          <div className={styles.metricsGrid}>
            {sortedMetrics.map((om) => (
              <div key={om.metricGuid} className={styles.metricCard}>
                <div className={styles.metricCardLabel}>{om.label}</div>
                <div className={styles.metricCardValue}>
                  {om.actualValue != null ? om.actualValue : '—'}{om.unit ? ` ${om.unit}` : ''}
                </div>
                {om.targetValue != null && (
                  <div className={styles.metricCardSub}>
                    {t('chronicleView.metricTarget')}: {om.targetValue}{om.unit ? ` ${om.unit}` : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Related news ── */}
      {newsLinks.length > 0 && (
        <section className={styles.proseSection}>
          <h2 className={styles.proseSectionTitle}>{t('chronicleView.detailsNews')}</h2>
          <div className={styles.newsCardList}>
            {newsLinks.map((lnk) => (
              <a key={lnk.linkGuid} href={lnk.url} target="_blank" rel="noopener noreferrer" className={styles.newsCard}>
                {lnk.ogImageUrl ? (
                  <img className={styles.newsCardImage} src={lnk.ogImageUrl} alt="" />
                ) : (
                  <div className={styles.newsCardImagePlaceholder}>📰</div>
                )}
                <div className={styles.newsCardBody}>
                  <div className={styles.newsCardTitle}>{lnk.ogTitle || lnk.label}</div>
                  <div className={styles.newsCardMeta}>
                    {[lnk.ogSiteName, lnk.ogAuthor, lnk.ogPublishedAt ? new Date(lnk.ogPublishedAt).toLocaleDateString() : null].filter(Boolean).join(' · ')}
                  </div>
                  {lnk.ogDescription && <div className={styles.newsCardDesc}>{lnk.ogDescription}</div>}
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* ── Other links ── */}
      {otherLinks.length > 0 && (
        <section className={styles.proseSection}>
          <h2 className={styles.proseSectionTitle}>{t('chronicleView.detailsLinks')}</h2>
          <div className={styles.linkList}>
            {otherLinks.map((lnk) => (
              <a key={lnk.linkGuid} href={lnk.url} target="_blank" rel="noopener noreferrer" className={styles.linkChip}>
                <span className={styles.linkType}>{lnk.linkType}</span>
                {lnk.label}
              </a>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
